"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";

import { DropdownItem, DropdownMenu } from "@/components/ds/dropdown";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";
import { cn } from "@/lib/utils/cn";
import type { DeviceDto } from "@/lib/api/types";

export type DeviceActionsDropdownProps = {
  device: DeviceDto;
  onExternalDataAccess?: (d: DeviceDto) => void;
  onAssignUsers?: (d: DeviceDto) => void;
  onBackup?: (d: DeviceDto) => void;
  onBackupDelete?: (d: DeviceDto) => void;
  onDelete?: (d: DeviceDto) => void;
  onDisable?: (d: DeviceDto) => void;
  className?: string;
};

export function DeviceActionsDropdown({ device, onExternalDataAccess, onAssignUsers, onBackup, onBackupDelete, onDelete, onDisable, className }: DeviceActionsDropdownProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { permissionMap } = useEffectivePermissions();
  const canEditDevices = hasModulePermission(user, permissionMap, "devices", "edit");
  const canCreateOta = hasModulePermission(user, permissionMap, "ota", "create");
  const [toast, setToast] = useState<string | null>(null);

  const openEditTab = (tab: string) => {
    if (!canEditDevices) {
      setToast("You don't have permission to edit data loggers.");
      return;
    }
    navigate(`/devices/${device.id}/edit?tab=${tab}`);
  };

  const openOta = () => {
    if (!canCreateOta) {
      setToast("You don't have permission to create OTA rollouts.");
      return;
    }
    navigate(`/ota/new?device=${device.id}`);
  };

  const openExternalDataAccess = () => {
    if (!canEditDevices) {
      setToast("You don't have permission to manage external data access.");
      return;
    }
    navigate(`/devices/${device.id}/external-access`);
  };

  return (
    <>
      <DropdownMenu
        align="right"
        className={cn(className)}
        trigger={
          <span
            className="inline-flex cursor-pointer items-center gap-ds-1 rounded-lg border border-border/70 bg-card px-ds-2 py-ds-1 text-caption text-foreground shadow-sm transition-colors hover:bg-muted-bg/50"
            title="Quick actions"
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden />
          </span>
        }
      >
        <DropdownItem onClick={openOta}>Send OTA</DropdownItem>
        {onExternalDataAccess ? <DropdownItem onClick={openExternalDataAccess}>External data access</DropdownItem> : null}
        {onAssignUsers ? <DropdownItem onClick={() => onAssignUsers(device)}>Assign users</DropdownItem> : null}
        {onDisable && canEditDevices ? <DropdownItem onClick={() => onDisable(device)}>Disable device</DropdownItem> : null}
        <DropdownItem onClick={() => openEditTab("debug")}>View logs</DropdownItem>
        {onBackup ? <DropdownItem onClick={() => onBackup(device)}>Take backup</DropdownItem> : null}
        {onBackupDelete ? (
          <DropdownItem className="text-error hover:bg-error/10" onClick={() => onBackupDelete(device)}>
            Backup & delete
          </DropdownItem>
        ) : null}
        {onDelete ? (
          <DropdownItem className="text-error hover:bg-error/10" onClick={() => onDelete(device)}>
            Delete
          </DropdownItem>
        ) : null}
      </DropdownMenu>
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </>
  );
}
